// Hero.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConsultationModal from './ConsultationModal';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showConsultationModal, setShowConsultationModal] = useState(false);

  const slides = [
    {
      tag: 'Moving abroad',
      heading: 'Plan Your Move Abroad\nOne Step at a Time',
      description: 'Ivipass works with people who want to study or take a job in another country. We talk through where you stand, set out what each route asks of you, and stay with the paperwork until it is submitted.',
      primaryCTA: { text: 'Arrange a Consultation', action: 'consultation' },
      secondaryCTA: { text: 'Ask a Question First', action: 'consultation' },
      image: 'hero-1.jpg',
      serviceType: 'study-abroad' // Default to study-abroad for general consultation
    },
    {
      tag: 'Studying',
      heading: 'From a Long List\nTo a Confirmed Place',
      description: 'Picking courses is only the start. We help you build a realistic list of institutions, sharpen your personal statement, keep track of what each one wants, and get everything filed before the deadline closes.',
      primaryCTA: { text: 'See Study Options', link: '/study-abroad' },
      secondaryCTA: { text: 'Talk About Studying', action: 'consultation' },
      image: 'hero-2.jpg',
      serviceType: 'study-abroad'
    },
    {
      tag: 'Working',
      heading: 'Overseas Roles and\nthe Prep to Land Them',
      description: 'We put forward candidates who match what an overseas employer is asking for, then work on the interview itself: the format, the questions, and the documents you will be asked to produce. You are told at each stage where you stand.',
      primaryCTA: { text: 'See Work Options', link: '/work-abroad' },
      secondaryCTA: { text: 'Talk About Working', action: 'consultation' },
      image: 'hero-3.jpg',
      serviceType: 'work-abroad'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 7000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative w-full h-[600px] md:h-[700px] overflow-hidden bg-white">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0"
        >
          <div className="max-w-7xl mx-auto h-full grid md:grid-cols-2">
            {/* Left Content */}
            <div className="flex flex-col justify-center px-4 md:px-8 py-12 bg-surface">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="inline-block bg-white text-secondary text-xs font-light px-4 py-2 rounded-md mb-4">
                  {slides[currentSlide].tag}
                </span>
                
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-primary leading-tight mb-6 whitespace-pre-line">
                  {slides[currentSlide].heading}
                </h1>
                
                <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl font-light">
                  {slides[currentSlide].description}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  {slides[currentSlide].primaryCTA.link ? (
                    <Link
                      to={slides[currentSlide].primaryCTA.link}
                      className="bg-primary hover:bg-primary-hover text-white font-normal text-sm px-8 py-3 rounded-lg transition-colors text-center"
                    >
                      {slides[currentSlide].primaryCTA.text}
                    </Link>
                  ) : (
                    <button
                      onClick={() => setShowConsultationModal(true)}
                      className="bg-primary hover:bg-primary-hover text-white font-normal text-sm px-8 py-3 rounded-lg transition-colors text-center"
                    >
                      {slides[currentSlide].primaryCTA.text}
                    </button>
                  )}
                  {slides[currentSlide].secondaryCTA.link ? (
                    <Link
                      to={slides[currentSlide].secondaryCTA.link}
                      className="border border-gray-300 hover:border-primary hover:text-primary text-gray-700 font-normal text-sm px-8 py-3 rounded-lg transition-colors text-center"
                    >
                      {slides[currentSlide].secondaryCTA.text}
                    </Link>
                  ) : (
                    <button
                      onClick={() => setShowConsultationModal(true)}
                      className="border border-gray-300 hover:border-primary hover:text-primary text-gray-700 font-normal text-sm px-8 py-3 rounded-lg transition-colors text-center"
                    >
                      {slides[currentSlide].secondaryCTA.text}
                    </button>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Right Image */}
            <div className="relative hidden md:block">
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ 
                  backgroundImage: `url(${require(`../assets/${slides[currentSlide].image}`)})`,
                }}
              />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentSlide ? 'bg-primary w-8' : 'bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Consultation Modal */}
      <ConsultationModal 
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        serviceType={slides[currentSlide].serviceType}
      />
    </section>
  );
};

export default Hero;
